// ============================================
// FAQ 知识中心 — 独立 Layout（参照 PriceIntelLayout）
// 管理面板：/:faqSpaceSlug/uadmin
// 用户面板：/:faqSpaceSlug
// ============================================

import { useEffect, useState, useRef } from 'react';
import {
  ChevronRight, HelpCircle, Menu, X,
  LayoutDashboard, BookOpen, Tag, Users, Trash2, MessageSquare,
  LogIn, LogOut, User, Eye, EyeOff, Loader2, Lock, UserCircle, RefreshCw, LifeBuoy, ShieldCheck,
} from 'lucide-react';
import { NavLink, Outlet, useLocation, useParams, Link, useNavigate } from 'react-router-dom';
import { useFaqStore } from './faq-store';
import { useFaqQuestionStore } from './faq-question-store';
import { useAuthStore } from '../stores/auth-store';
import { useUserStore } from '../stores/user-store';
import FaqAuthModal from './FaqAuthModal';
import FaqProfileModal from './FaqProfileModal';
import { cn } from '../utils/cn';
import { supabase } from '../config/supabase';
import { FaqSpaceProvider } from './FaqSpaceContext';
import { buildFaqAdminPath, buildFaqPath, normalizeFaqSpaceSlug } from './faq-space-utils';
import type { FaqSpace } from './types';

type NavItem = {
  to: string;
  label: string;
  icon: typeof HelpCircle;
  end?: boolean;
};

type BreadcrumbItem = {
  label: string;
  fullLabel?: string;
  to?: string;
};

function truncateBreadcrumbLabel(value: string, maxLength = 20) {
  const chars = Array.from(value.trim());
  if (chars.length <= maxLength) return value.trim();
  return `${chars.slice(0, maxLength).join('')}...`;
}

function getCurrentTitle(pathname: string, navItems: NavItem[]): string {
  for (const item of navItems) {
    if (item.end ? pathname === item.to : pathname.startsWith(item.to)) {
      return item.label;
    }
  }
  return 'FAQ';
}

type FaqBrandLinkProps = {
  to: string;
  title: string;
  mode?: 'admin' | 'public';
  variant?: 'header' | 'sidebar';
  onClick?: () => void;
  className?: string;
};

function FaqBrandLink({ to, title, mode = 'public', variant = 'header', onClick, className }: FaqBrandLinkProps) {
  const isSidebar = variant === 'sidebar';

  return (
    <Link
      to={to}
      onClick={onClick}
      className={cn(
        'group flex min-w-0 items-center gap-2 rounded-xl transition',
        isSidebar ? 'px-1.5 py-1' : 'shrink-0 px-2 py-1.5 hover:bg-sky-50',
        className,
      )}
      aria-label={`返回 ${title}`}
    >
      <span
        className={cn(
          'flex shrink-0 items-center justify-center rounded-xl border border-sky-100 bg-sky-50 text-sky-600 shadow-sm shadow-sky-100/60 transition group-hover:border-sky-200 group-hover:bg-sky-100',
          isSidebar ? 'h-9 w-9' : 'h-8 w-8',
        )}
      >
        <LifeBuoy size={isSidebar ? 17 : 15} />
      </span>
      <span className="min-w-0 leading-tight">
        <span
          className={cn(
            'block truncate font-semibold text-slate-800 transition group-hover:text-sky-600',
            isSidebar ? 'text-[15px]' : 'hidden text-sm sm:block',
          )}
        >
          {title}
        </span>
        {mode === 'admin' && (
          <span className="block truncate text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
            管理后台
          </span>
        )}
      </span>
    </Link>
  );
}

interface FaqLayoutProps {
  mode: 'admin' | 'public';
}

export default function FaqLayout({ mode }: FaqLayoutProps) {
  const location = useLocation();
  const { faqSpaceSlug, questionId } = useParams<{ faqSpaceSlug?: string; questionId?: string }>();
  const normalizedSpaceSlug = normalizeFaqSpaceSlug(faqSpaceSlug);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [space, setSpace] = useState<FaqSpace | null>(null);
  const [spaceLoading, setSpaceLoading] = useState(true);
  const [spaceError, setSpaceError] = useState('');
  const userMenuRef = useRef<HTMLDivElement>(null);

  // 邀请设置密码弹窗
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [setupName, setSetupName] = useState('');
  const [setupPassword, setSetupPassword] = useState('');
  const [setupConfirm, setSetupConfirm] = useState('');
  const [setupShowPwd, setSetupShowPwd] = useState(false);
  const [setupSaving, setSetupSaving] = useState(false);
  const [setupError, setSetupError] = useState('');
  const faqItems = useFaqStore((s) => s.items);
  const faqQuestions = useFaqQuestionStore((s) => s.questions);
  const currentQuestion = useFaqQuestionStore((s) => s.currentQuestion);
  const subscribeRealtime = useFaqStore((s) => s.subscribeRealtime);
  const unsubscribeRealtime = useFaqStore((s) => s.unsubscribeRealtime);
  const realtimeToast = useFaqStore((s) => s.realtimeToast);
  const dismissToast = useFaqStore((s) => s.dismissToast);
  const setFaqStoreSpace = useFaqStore((s) => s.setSpace);
  const setFaqQuestionStoreSpace = useFaqQuestionStore((s) => s.setSpace);
  const { user, signOut } = useAuthStore();
  const { profile, isLoading: profileLoading, fetchProfile } = useUserStore();
  const basePath = space ? buildFaqPath(space) : buildFaqPath(normalizedSpaceSlug);
  const adminBasePath = space ? buildFaqAdminPath(space) : buildFaqAdminPath(normalizedSpaceSlug);
  const faqPath = (path?: string) => space ? buildFaqPath(space, path) : buildFaqPath(normalizedSpaceSlug, path);
  const faqAdminPath = (path?: string) => space ? buildFaqAdminPath(space, path) : buildFaqAdminPath(normalizedSpaceSlug, path);

  useEffect(() => {
    let cancelled = false;
    setSpaceLoading(true);
    setSpaceError('');
    setSpace(null);
    setFaqStoreSpace(null);
    setFaqQuestionStoreSpace(null);

    supabase
      .from('faq_spaces')
      .select('*')
      .eq('slug', normalizedSpaceSlug)
      .eq('is_active', true)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.error('Resolve FAQ space failed:', error);
          setSpaceError(error.message || 'FAQ 空间加载失败');
          setSpaceLoading(false);
          return;
        }
        if (!data) {
          setSpaceError('未找到该 FAQ 空间');
          setSpaceLoading(false);
          return;
        }
        const resolvedSpace = data as FaqSpace;
        setFaqStoreSpace(resolvedSpace);
        setFaqQuestionStoreSpace(resolvedSpace);
        setSpace(resolvedSpace);
        setSpaceLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [normalizedSpaceSlug, setFaqStoreSpace, setFaqQuestionStoreSpace]);

  // 用户登录后自动加载 profile（含 is_faq_admin）
  useEffect(() => {
    if (user && !profile) {
      fetchProfile();
    }
  }, [user, profile, fetchProfile]);

  // 邮件确认 / OTP / 其他标签页登录成功后，自动关闭 FAQ 登录注册弹窗
  useEffect(() => {
    if (user && showAuthModal) {
      setShowAuthModal(false);
    }
  }, [user, showAuthModal]);

  // 启动 Realtime 订阅（faq_items / faq_tags 变更自动同步）
  useEffect(() => {
    if (!space) return;
    subscribeRealtime();
    return () => unsubscribeRealtime();
  }, [space, subscribeRealtime, unsubscribeRealtime]);

  // Toast 自动消失（3秒）
  useEffect(() => {
    if (!realtimeToast) return;
    const timer = setTimeout(() => dismissToast(), 3000);
    return () => clearTimeout(timer);
  }, [realtimeToast, dismissToast]);

  // 检测邀请用户：页面加载时检查 user_metadata
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user: currentUser } }) => {
      if (currentUser?.user_metadata?.needs_password_setup) {
        setShowSetupModal(true);
      }
    });
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // 点击外部关闭用户菜单
  useEffect(() => {
    if (!showUserMenu) return;
    function handleClick(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showUserMenu]);

  const navigate = useNavigate();

  // 退出登录：跳转到 FAQ 公开页而非全局登录页
  async function handleSignOut() {
    setShowUserMenu(false);
    try {
      await signOut();
    } catch {
      // 静默处理
    }
    navigate(basePath);
  }

  // 邀请用户设置账户
  async function handleSetupAccount() {
    if (!setupPassword.trim()) {
      setSetupError('请输入密码');
      return;
    }
    if (setupPassword.length < 6) {
      setSetupError('密码至少 6 个字符');
      return;
    }
    if (setupPassword !== setupConfirm) {
      setSetupError('两次密码不一致');
      return;
    }

    setSetupSaving(true);
    setSetupError('');
    try {
      // 设置密码并清除邀请标记
      const { error: pwdError } = await supabase.auth.updateUser({
        password: setupPassword,
        data: { needs_password_setup: false },
      });
      if (pwdError) throw pwdError;

      // 设置姓名（写入 fs_user_profiles）
      if (setupName.trim()) {
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        if (currentUser) {
          await supabase
            .from('fs_user_profiles')
            .update({ name: setupName.trim() })
            .eq('id', currentUser.id);
          // 同时更新 core_users
          await supabase
            .from('core_users')
            .update({ name: setupName.trim() })
            .eq('id', currentUser.id);
        }
      }

      setShowSetupModal(false);
      // 重新加载 profile
      fetchProfile();
    } catch (e: unknown) {
      setSetupError(e instanceof Error ? e.message : '设置失败');
    } finally {
      setSetupSaving(false);
    }
  }

  // 设置密码弹窗（public + admin 共用）
  function renderSetupModal() {
    if (!showSetupModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
          <div className="text-center px-6 pt-8 pb-2">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center mx-auto mb-4">
              <UserCircle size={28} className="text-white" />
            </div>
            <h3 className="text-lg font-bold text-slate-800">欢迎加入！</h3>
            <p className="text-sm text-slate-400 mt-1">请设置您的姓名和登录密码</p>
          </div>
          <div className="px-6 py-5 space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5">姓名 / Name</label>
              <div className="relative">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input type="text" value={setupName} onChange={(e) => setSetupName(e.target.value)}
                  placeholder="请输入您的姓名"
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 transition-all"
                  autoFocus />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5">设置密码 <span className="text-red-400">*</span></label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input type={setupShowPwd ? 'text' : 'password'} value={setupPassword}
                  onChange={(e) => setSetupPassword(e.target.value)} placeholder="至少 6 个字符"
                  className="w-full pl-10 pr-10 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 transition-all" />
                <button type="button" onClick={() => setSetupShowPwd(!setupShowPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {setupShowPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5">确认密码 <span className="text-red-400">*</span></label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input type={setupShowPwd ? 'text' : 'password'} value={setupConfirm}
                  onChange={(e) => setSetupConfirm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSetupAccount()}
                  placeholder="再次输入密码"
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 transition-all" />
              </div>
            </div>
            {setupError && (
              <p className="text-xs text-red-500 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{setupError}</p>
            )}
            <button onClick={handleSetupAccount} disabled={setupSaving || !setupPassword.trim()}
              className="w-full py-2.5 bg-indigo-500 text-white text-sm font-semibold rounded-xl hover:bg-indigo-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
              {setupSaving ? <Loader2 size={16} className="animate-spin" /> : null}
              {setupSaving ? '保存中...' : '完成设置'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 用户状态组件
  function UserStatusArea() {
    if (user) {
      const emailPrefix = user.email?.split('@')[0] || '用户';
      const displayName = profile?.name || user.user_metadata?.name || emailPrefix;
      const avatarUrl = profile?.avatar_url || user.user_metadata?.avatar_url || '';
      const wecomUserid = profile?.wecom_userid || user.user_metadata?.wecom_userid || '';
      const initial = (displayName[0] || 'U').toUpperCase();
      return (
        <div className="relative" ref={userMenuRef}>
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <span className="w-7 h-7 overflow-hidden rounded-full bg-gradient-to-br from-sky-400 to-indigo-500 text-white text-xs font-bold flex items-center justify-center">
              {avatarUrl ? <img src={avatarUrl} alt={displayName} className="h-full w-full object-cover" /> : initial}
            </span>
            <span className="text-xs text-slate-600 font-medium max-w-[120px] truncate hidden sm:block">
              {displayName}
            </span>
          </button>
          {showUserMenu && (
            <div className="absolute right-0 top-full mt-1.5 w-64 bg-white border border-slate-200 rounded-xl shadow-lg py-1.5 z-50">
              <div className="flex items-center gap-3 px-3 py-3 border-b border-slate-100">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-sky-400 to-indigo-500 text-sm font-bold text-white">
                  {avatarUrl ? <img src={avatarUrl} alt={displayName} className="h-full w-full object-cover" /> : initial}
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-700">{displayName}</p>
                  <p className="truncate text-xs text-slate-400">{user.email}</p>
                  {wecomUserid && (
                    <p className="mt-0.5 truncate text-[11px] text-slate-400">{wecomUserid}</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => {
                  setShowUserMenu(false);
                  setShowProfileModal(true);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 transition-colors"
              >
                <UserCircle size={14} />
                个人中心
              </button>
              <button
                onClick={handleSignOut}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 transition-colors"
              >
                <LogOut size={14} />
                退出登录
              </button>
            </div>
          )}
        </div>
      );
    }
    return (
      <button
        onClick={() => setShowAuthModal(true)}
        className="flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold text-sky-600 bg-sky-50 border border-sky-200 rounded-lg hover:bg-sky-100 hover:border-sky-300 transition-all"
      >
        <LogIn size={14} />
        登录
      </button>
    );
  }

  const isSuperAdmin = profile?.role === 'super_admin';
  const isFaqAdmin = !!profile?.is_faq_admin;
  const canUseFaqAdmin = isFaqAdmin || isSuperAdmin;
  const spaceTitle = space?.title || 'FAQ 支持中心';
  const adminNavBase: NavItem[] = [
    { to: adminBasePath, label: 'FAQ 管理', icon: LayoutDashboard, end: true },
    { to: faqAdminPath('questions'), label: '留言管理', icon: MessageSquare },
    { to: faqAdminPath('tags'), label: '标签管理', icon: Tag },
    { to: faqAdminPath('trash'), label: '回收站', icon: Trash2 },
  ];
  const adminNavWithUsers: NavItem[] = [
    ...adminNavBase,
    { to: faqAdminPath('users'), label: '用户管理', icon: Users },
  ];
  const publicNav: NavItem[] = [
    { to: basePath, label: '支持首页', icon: LifeBuoy, end: true },
    { to: faqPath('knowledge'), label: '知识问答', icon: BookOpen },
    { to: faqPath('questions'), label: '留言中心', icon: MessageSquare },
  ];
  const publicAdminNavItem: NavItem = {
    to: adminBasePath,
    label: '管理中心',
    icon: LayoutDashboard,
  };
  const publicNavItems = canUseFaqAdmin ? [...publicNav, publicAdminNavItem] : publicNav;
  const navItems = mode === 'admin'
    ? (isSuperAdmin ? adminNavWithUsers : adminNavBase)
    : publicNavItems;
  const currentTitle = getCurrentTitle(location.pathname, navItems);

  function renderAdminAccessGate(kind: 'login' | 'forbidden' | 'loading') {
    const isLogin = kind === 'login';
    const isLoadingGate = kind === 'loading';
    const title = isLoadingGate ? '正在检查权限' : isLogin ? '请先登录' : '无 FAQ 管理权限';
    const description = isLoadingGate
      ? '正在读取你的 FAQ 管理员资料。'
      : isLogin
        ? '登录后才能进入 FAQ 管理中心。'
        : '当前账号不是 FAQ 管理员，请返回支持首页。';

    return (
      <div className="flex h-screen items-center justify-center bg-slate-50 px-4 text-slate-800">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-sky-50 text-sky-600">
            {isLoadingGate ? <Loader2 size={24} className="animate-spin" /> : isLogin ? <LogIn size={24} /> : <ShieldCheck size={24} />}
          </div>
          <h1 className="text-lg font-bold text-slate-800">{title}</h1>
          <p className="mt-2 text-sm text-slate-500">{description}</p>
          <div className="mt-5 flex justify-center gap-2">
            {isLogin ? (
              <button
                onClick={() => setShowAuthModal(true)}
                className="rounded-xl bg-sky-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-sky-600"
              >
                登录 / 注册
              </button>
            ) : (
              <Link
                to={basePath}
                className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-600 transition hover:border-sky-200 hover:bg-sky-50 hover:text-sky-600"
              >
                返回支持首页
              </Link>
            )}
          </div>
        </div>
        <FaqAuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
      </div>
    );
  }

  if (spaceLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50 text-slate-500">
        <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
          <Loader2 size={18} className="animate-spin text-sky-500" />
          <span className="text-sm font-medium">正在加载 FAQ 空间...</span>
        </div>
      </div>
    );
  }

  if (!space) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50 px-4 text-slate-800">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-500">
            <HelpCircle size={24} />
          </div>
          <h1 className="text-lg font-bold text-slate-800">FAQ 空间不存在</h1>
          <p className="mt-2 text-sm text-slate-500">{spaceError || '当前链接没有对应的 FAQ 空间。'}</p>
          <Link
            to="/faq"
            className="mt-5 inline-flex rounded-xl bg-sky-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-sky-600"
          >
            返回默认 FAQ
          </Link>
        </div>
      </div>
    );
  }

  const sidebar = (
    <aside className={cn(
      'flex h-full w-[240px] shrink-0 flex-col border-r border-slate-200 bg-white',
    )}>
      {/* Logo 区域 */}
      <div className="flex h-[56px] items-center justify-between border-b border-slate-100 px-4">
        <FaqBrandLink to={basePath} title={spaceTitle} mode={mode} variant="sidebar" onClick={() => setMobileOpen(false)} />
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          className="rounded-md p-1.5 text-slate-400 transition hover:bg-slate-100 lg:hidden"
          aria-label="关闭菜单"
        >
          <X size={16} />
        </button>
      </div>

      {/* 导航 */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <div className="space-y-0.5">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'group flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium transition',
                  isActive
                    ? 'bg-sky-50 text-sky-600'
                    : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700',
                )
              }
            >
              <item.icon size={16} />
              {item.label}
            </NavLink>
          ))}
        </div>

        {/* 管理模式下，显示用户端入口链接 */}
        {mode === 'admin' && (
          <div className="mt-6 pt-4 border-t border-slate-100">
            <p className="px-2 mb-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">
              快捷跳转
            </p>
            <NavLink
              to={basePath}
              className="flex items-center gap-2.5 rounded-lg border border-sky-100 bg-sky-50 px-2.5 py-2 text-sm font-semibold text-sky-600 transition hover:border-sky-200 hover:bg-sky-100"
            >
              <LifeBuoy size={16} />
              返回支持首页
            </NavLink>
          </div>
        )}
      </nav>
    </aside>
  );

  // public 模式：不需要系统侧边栏，FaqPublicPage 自带筛选侧边栏
  if (mode === 'public') {
    // 从 URL 提取详情页 id
    const pathParts = location.pathname.split('/').filter(Boolean);
    const relativeParts = location.pathname.slice(basePath.length).split('/').filter(Boolean);
    const isQuestionsPath = relativeParts[0] === 'questions';
    const activeQuestionId = questionId || (isQuestionsPath && relativeParts.length >= 2 ? relativeParts[1] : undefined);
    const reservedPublicPaths = new Set(['questions', 'knowledge']);
    const detailId = relativeParts.length >= 1 && !reservedPublicPaths.has(relativeParts[0]) ? relativeParts[0] : null;
    const detailItem = detailId ? faqItems.find((i) => i.id === detailId) : null;
    const questionTitle = activeQuestionId
      ? currentQuestion?.id === activeQuestionId
        ? currentQuestion.title
        : faqQuestions.find((item) => item.id === activeQuestionId)?.title
      : null;
    const questionBreadcrumbLabel = questionTitle
      ? truncateBreadcrumbLabel(questionTitle, 20)
      : '留言详情';
    const publicTitle = isQuestionsPath
      ? (relativeParts.length >= 2 ? '留言详情' : '留言中心')
      : relativeParts[0] === 'knowledge' ? '知识问答' : '支持首页';
    const breadcrumbItems: BreadcrumbItem[] = detailItem
      ? [
          { label: '知识问答', to: faqPath('knowledge') },
          { label: truncateBreadcrumbLabel(detailItem.question, 20), fullLabel: detailItem.question },
        ]
      : isQuestionsPath
        ? [
            relativeParts.length >= 2
              ? { label: '留言中心', to: faqPath('questions') }
              : { label: '留言中心' },
            ...(relativeParts.length >= 2 ? [{ label: questionBreadcrumbLabel, fullLabel: questionTitle || questionBreadcrumbLabel }] : []),
          ]
        : location.pathname === basePath
          ? []
          : [{ label: publicTitle }];

    return (
      <FaqSpaceProvider space={space}>
      <div className="flex h-screen flex-col overflow-hidden bg-slate-50 text-slate-800">
        <header className="flex h-[56px] shrink-0 items-center justify-between gap-4 border-b border-slate-200 bg-white px-3 sm:px-4 lg:px-6">
          <nav className="flex min-w-0 flex-1 items-center gap-1 text-sm" aria-label="FAQ 面包屑">
            <FaqBrandLink to={basePath} title={spaceTitle} variant="header" />
            {breadcrumbItems.map((item, index) => (
              <div key={`${item.label}-${index}`} className="flex min-w-0 items-center gap-1">
                <ChevronRight size={14} className="shrink-0 text-slate-300" />
                {item.to ? (
                  <Link
                    to={item.to}
                    className="max-w-[130px] truncate font-medium text-slate-500 transition hover:text-sky-600 sm:max-w-none"
                    title={item.fullLabel || item.label}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <span
                    className="max-w-[180px] truncate font-semibold text-slate-800 sm:max-w-[360px]"
                    title={item.fullLabel || item.label}
                  >
                    {item.label}
                  </span>
                )}
              </div>
            ))}
          </nav>

          <div className="flex shrink-0 items-center gap-2">
            <nav className="hidden items-center gap-1 rounded-xl border border-slate-200 bg-slate-50 p-1 lg:flex" aria-label="FAQ 模块导航">
              {publicNavItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    cn(
                      'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition',
                      isActive
                        ? 'bg-white text-sky-600 shadow-sm'
                        : 'text-slate-500 hover:bg-white hover:text-slate-700',
                    )
                  }
                >
                  <item.icon size={14} />
                  {item.label}
                </NavLink>
              ))}
            </nav>

            <nav className="flex items-center gap-1 rounded-xl border border-slate-200 bg-slate-50 p-1 lg:hidden" aria-label="FAQ 模块导航">
              {publicNavItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    cn(
                      'inline-flex h-8 w-8 items-center justify-center rounded-lg transition sm:w-auto sm:px-2.5',
                      isActive ? 'bg-white text-sky-600 shadow-sm' : 'text-slate-500 hover:bg-white hover:text-slate-700',
                    )
                  }
                  title={item.label}
                  aria-label={item.label}
                >
                  <item.icon size={15} />
                  <span className="ml-1.5 hidden text-xs font-semibold sm:inline">{item.label}</span>
                </NavLink>
              ))}
            </nav>

            <UserStatusArea />
          </div>
        </header>
        <main className="min-h-0 flex-1 overflow-hidden">
          <Outlet />
        </main>
        <FaqAuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
        <FaqProfileModal isOpen={showProfileModal} onClose={() => setShowProfileModal(false)} />
        {renderSetupModal()}

        {/* Realtime 更新气泡 */}
        {realtimeToast && (
          <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 animate-[slideDown_0.3s_ease-out]">
            <div className="flex items-center gap-2 px-5 py-2.5 bg-white rounded-full shadow-lg border border-slate-100">
              <RefreshCw size={14} className="text-sky-500 animate-spin" />
              <span className="text-sm text-slate-600 font-medium">{realtimeToast}</span>
              <button
                onClick={dismissToast}
                className="ml-1 text-slate-300 hover:text-slate-500 transition-colors"
              ><X size={14} /></button>
            </div>
          </div>
        )}
      </div>
      </FaqSpaceProvider>
    );
  }

  if (mode === 'admin' && !user) {
    return <FaqSpaceProvider space={space}>{renderAdminAccessGate('login')}</FaqSpaceProvider>;
  }

  if (mode === 'admin' && user && (!profile || profileLoading)) {
    return <FaqSpaceProvider space={space}>{renderAdminAccessGate('loading')}</FaqSpaceProvider>;
  }

  if (mode === 'admin' && !canUseFaqAdmin) {
    return <FaqSpaceProvider space={space}>{renderAdminAccessGate('forbidden')}</FaqSpaceProvider>;
  }

  // admin 模式：保留系统侧边栏
  return (
    <FaqSpaceProvider space={space}>
    <div className="flex h-screen overflow-hidden bg-slate-50 text-slate-800">
      {/* 桌面端侧边栏 */}
      <div className="hidden h-screen shrink-0 lg:block">{sidebar}</div>

      {/* 移动端遮罩 + 侧边栏 */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/30 lg:hidden"
            onClick={() => setMobileOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 z-50 lg:hidden">{sidebar}</div>
        </>
      )}

      {/* 主内容区 */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-[56px] shrink-0 items-center justify-between border-b border-slate-200 bg-white px-4 lg:px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="rounded-md p-1.5 text-slate-400 transition hover:bg-slate-100 lg:hidden"
              aria-label="打开菜单"
            >
              <Menu size={18} />
            </button>
            <div className="flex items-center gap-2 text-sm">
              <FaqBrandLink to={basePath} title={spaceTitle} variant="header" className="-ml-2" />
              <span className="text-slate-300">/</span>
              <span className="font-medium text-slate-700">{currentTitle}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to={basePath}
              className="hidden items-center gap-1.5 rounded-lg border border-sky-100 bg-sky-50 px-3 py-1.5 text-xs font-semibold text-sky-600 transition hover:border-sky-200 hover:bg-sky-100 sm:inline-flex"
            >
              <LifeBuoy size={14} />
              返回支持首页
            </Link>
            <UserStatusArea />
          </div>
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
      <FaqAuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
      <FaqProfileModal isOpen={showProfileModal} onClose={() => setShowProfileModal(false)} />
      {renderSetupModal()}

      {/* Realtime 更新气泡 */}
      {realtimeToast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 animate-[slideDown_0.3s_ease-out]">
          <div className="flex items-center gap-2 px-5 py-2.5 bg-white rounded-full shadow-lg border border-slate-100">
            <RefreshCw size={14} className="text-sky-500 animate-spin" />
            <span className="text-sm text-slate-600 font-medium">{realtimeToast}</span>
            <button
              onClick={dismissToast}
              className="ml-1 text-slate-300 hover:text-slate-500 transition-colors"
            ><X size={14} /></button>
          </div>
        </div>
      )}
    </div>
    </FaqSpaceProvider>
  );
}
